import React, {
  ReactNode,
  createContext,
  useContext,
  useEffect,
  useMemo,
  useState,
  useCallback,
} from "react";
import { Preferences } from "@capacitor/preferences";
import { ClearPresistor } from "../state/Store";
import { IonApp, IonLoading } from "@ionic/react";
import Login from "../../pages/Login";
import { useSelector } from "react-redux";
import { RestApi } from "../../services/Api";

// ================= TYPES =================

type Props = { children?: ReactNode };

interface User {
  [key: string]: any;
}

interface AuthState {
  initialized: boolean;
  loggedIn: boolean;
  user: User | null;
}

interface AuthContextType {
  authInfo: AuthState;
  initialize: () => Promise<void>;
  logOut: () => Promise<void>;
  logIn: (userData: User) => Promise<void>;
}

// ================= CONTEXT =================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// ================= HELPER =================

const isUserValid = (user: any): user is User => user && typeof user === "object" && Object.keys(user).length > 0;

// ================= PROVIDER =================

const AuthProvider = ({ children }: Props) => {
  const reduxUser = useSelector(
    (state: { user: { value: User | null } }) => state.user.value,
  );

  const [authInfo, setAuthInfo] = useState<AuthState>({
    initialized: false,
    loggedIn: false,
    user: null,
  });

  // ---------- INITIALIZE ----------
  const initialize = useCallback(async () => {
    const { value: token } = await Preferences.get({ key: "token" });

    if (token && isUserValid(reduxUser)) {
      setAuthInfo({
        initialized: true,
        loggedIn: true,
        user: reduxUser,
      });
    } else {
      setAuthInfo({
        initialized: true,
        loggedIn: false,
        user: null,
      });
    }
  }, [reduxUser]);

  // ---------- LOGIN ----------
  const logIn = useCallback(async (userData: User) => {
    if (!isUserValid(userData)) {
      setAuthInfo({
        initialized: true,
        loggedIn: false,
        user: null,
      });
      return;
    }

    setAuthInfo({
      initialized: true,
      loggedIn: true,
      user: userData,
    });
  }, []);

  // ---------- LOGOUT ----------
  const logOut = useCallback(async () => {
    try {
      await RestApi("user/logout", "get", {}, "application/json");
    } catch {
      // silent fail, tetap logout
    }

    await Preferences.clear();
    window.localStorage.removeItem("_capuid");
    window.localStorage.removeItem("email");
    window.localStorage.removeItem("password");
    ClearPresistor();

    setAuthInfo({
      initialized: true,
      loggedIn: false,
      user: null,
    });
  }, []);

  // ---------- WATCH REDUX USER ----------
  useEffect(() => {
    if (authInfo.initialized) {
      if (!isUserValid(reduxUser)) {
        setAuthInfo((prev) => ({
          ...prev,
          loggedIn: false,
          user: null,
        }));
      } else {
        setAuthInfo((prev) => ({
          ...prev,
          loggedIn: true,
          user: reduxUser,
        }));
      }
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [reduxUser]);

  // ---------- FIRST LOAD ----------
  useEffect(() => {
    initialize();
  }, [initialize]);

  const value = useMemo(
    () => ({
      authInfo,
      initialize,
      logIn,
      logOut,
    }),
    [authInfo, initialize, logIn, logOut],
  );

  if (!authInfo.initialized) {
    return (
      <IonApp>
        <IonLoading isOpen />
      </IonApp>
    );
  }

  return (
    <AuthContext.Provider value={value}>
      {authInfo.loggedIn ? children : <Login />}
    </AuthContext.Provider>
  );
};

// ================= HOOK =================

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error("useAuth must be used inside AuthProvider");
  }
  return context;
};

export default AuthProvider;
